import { DefaultError, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { useStoreState } from 'providers/StoreStateContext';
import { IAuthResponse } from 'services/auth/types';
import { apiClient } from 'utils';
import { cacheService } from 'utils/service/cacheService';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IAccount, ISwitchAccount } from './types';
import toast from 'react-hot-toast';
import { ISelectOption } from 'types/components/inputs/input.interface';

// export const useGetAccounts = () => {
//   const { responseHandler } = useResponseHandler();
//   return useQuery<IAccount[], DefaultError>({
//     queryKey: ["accounts"],
//     async queryFn() {
//       const response = await apiClient({
//         method: "GET",
//         url: `Account/UserAccounts`,
//       });

//       return responseHandler(response, {
//         groupName: `useGetAccounts`,
//       });
//     },
//     ...cacheService(),
//   });
// };
export const useGetAccounts = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  const { tokenData } = useStoreState();

  const isEnabled = Boolean(enabled && tokenData?.CurrentAccount);

  return useQuery<IAccount[], DefaultError>({
    queryKey: ['accounts', tokenData?.CurrentAccount ?? 'no-account'],

    queryFn: async () => {
      const response = await apiClient({
        method: 'GET',
        url: `Account/UserAccounts`,
      });

      return responseHandler(response, {
        groupName: `useGetAccounts`,
      });
    },

    enabled: isEnabled,

    retry: false,

    refetchOnMount: false,

    ...cacheService(),
  });
};

export const useSwitchAccount = () => {
  const { responseHandler } = useResponseHandler();
  const queryClient = useQueryClient();
  const { loading, success, dismiss } = useQueryToast('useSwitchAccount');

  const { setToken, setRefreshToken, setServiceTokenExpiresAt, clearTokens } = useStoreState();

  return useMutation<IAuthResponse, DefaultError, ISwitchAccount>({
    async mutationFn({ token, accountId }) {
      loading(i18next.t('service-message.update.loading', { what: 'account' }));

      const response = await apiClient({
        method: 'POST',
        url: 'Account/SwitchAccount',
        params: { Token: token, AccountId: accountId },
      });

      return responseHandler(response, {
        groupName: 'useSwitchAccount',
        notFoundPath: '/',
        onError(status) {
          if (status === 403) {
            clearTokens();
            queryClient.clear();
            success(i18next.t('switch-account.forbidden', { what: 'account' }));
          }
        },
      });
    },

    async onSuccess(data) {
      setToken(data.token);
      setRefreshToken(data.refreshToken);

      setServiceTokenExpiresAt(data.expires);

      await queryClient.cancelQueries();
      queryClient.clear();

      success(i18next.t('service-message.update.success', { what: 'account' }));
    },

    onError: dismiss,
  });
};

export const useGetCopyrightList = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISelectOption<string>[], DefaultError>({
    queryKey: ['copyright-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'account/Copyright',
      });

      const data = await responseHandler<string[]>(response, {
        groupName: 'useGetCopyrightList',
      });

      return data.map((copyright) => ({ value: copyright, label: copyright }));
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
